import enLocale from '#marketing/locales/en.json';
import frLocale from '#marketing/locales/fr.json';
import zhLocale from '#marketing/locales/zh.json';
import {
  localePath,
  MARKETING_DEFAULT_LOCALE,
  parseLocaleFromPath,
} from '#marketing/lib/marketing-locale.js';
import { marketingMeta } from '#marketing/lib/marketing-meta.js';

/** @type {Record<string, typeof enLocale>} */
const localeResources = {
  en: enLocale,
  fr: frLocale,
  zh: zhLocale,
};

/**
 * @param {string} locale
 * @param {string} key - Dot path under locale JSON root
 * @returns {string}
 */
function translate(locale, key) {
  const parts = key.split('.');
  /** @type {unknown} */
  let value = localeResources[locale] ?? localeResources.en;
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = /** @type {Record<string, unknown>} */ (value)[part];
    } else {
      return key;
    }
  }
  return typeof value === 'string' ? value : key;
}

/**
 * Resolve locale from route matches (root loader or URL pathname).
 *
 * @param {Array<{ id?: string, pathname?: string, loaderData?: unknown }>} matches
 * @returns {string}
 */
export function getMarketingLocaleFromMatches(matches) {
  const rootMatch = matches.find((match) => match.id === 'root');
  if (
    rootMatch?.loaderData &&
    typeof rootMatch.loaderData === 'object' &&
    rootMatch.loaderData !== null
  ) {
    const locale = /** @type {{ locale?: string }} */ (rootMatch.loaderData).locale;
    if (locale) {
      return locale;
    }
  }

  const pathname = matches.at(-1)?.pathname ?? '/';
  return parseLocaleFromPath(pathname).locale;
}

/**
 * Build localized meta tags for a marketing page.
 *
 * @param {object} params
 * @param {Array<{ id?: string, pathname?: string, loaderData?: unknown }>} params.matches
 * @param {string} params.page
 * @param {string} params.pagePath
 * @param {string} [params.title]
 * @param {string} [params.description]
 * @returns {Array<Record<string, string>>}
 */
export function marketingRouteMeta({ matches, page, pagePath, title, description }) {
  const locale = getMarketingLocaleFromMatches(matches) ?? MARKETING_DEFAULT_LOCALE;

  return marketingMeta({
    locale,
    pagePath,
    path: localePath(locale, pagePath),
    title: title ?? translate(locale, `marketing.meta.${page}.title`),
    description: description ?? translate(locale, `marketing.meta.${page}.description`),
  });
}

/**
 * Meta for a published guide detail page. Title comes from the registry row.
 *
 * @param {object} params
 * @param {Array<{ id?: string, pathname?: string, loaderData?: unknown }>} params.matches
 * @param {{ id: string, title: string }} params.guide
 * @returns {Array<Record<string, string>>}
 */
export function marketingGuideRouteMeta({ matches, guide }) {
  const locale = getMarketingLocaleFromMatches(matches) ?? MARKETING_DEFAULT_LOCALE;
  const pagePath = `/guides/${guide.id}`;

  return marketingMeta({
    locale,
    pagePath,
    path: localePath(locale, pagePath),
    title: translate(locale, 'marketing.meta.guide.title').replaceAll('{{title}}', guide.title),
    description: translate(locale, 'marketing.meta.guide.description').replaceAll(
      '{{title}}',
      guide.title,
    ),
  });
}

/**
 * @param {Array<{ id?: string, pathname?: string, loaderData?: unknown }>} matches
 * @returns {string}
 */
export function getMarketingNotFoundTitle(matches) {
  const locale = getMarketingLocaleFromMatches(matches) ?? MARKETING_DEFAULT_LOCALE;
  return translate(locale, 'marketing.meta.notFound.title');
}
